
import { useTranslation } from "react-i18next"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Star, TrendingUp, AlertCircle, MessageSquare } from "lucide-react"
import { useReviewStats } from "@/api/reviews"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"

interface ReviewStatsDashboardProps {
  gymId?: string
}

export function ReviewStatsDashboard({ gymId }: ReviewStatsDashboardProps) {
  const { t } = useTranslation()
  const { data: stats, isLoading } = useReviewStats(gymId)

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Loading Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-24 bg-gray-200 animate-pulse rounded" />
                <div className="h-8 w-8 bg-gray-200 animate-pulse rounded-full" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-gray-200 animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Loading Chart */}
        <Card>
          <CardHeader>
            <div className="h-6 w-48 bg-gray-200 animate-pulse rounded" />
          </CardHeader>
          <CardContent>
            <div className="h-64 bg-gray-200 animate-pulse rounded" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('reviews.noReviews')}
      </div>
    )
  }

  // Prepare data for rating distribution chart
  const ratingDistData = Object.entries(stats.overall.ratingDistribution || {}).map(
    ([rating, count]) => ({
      rating: `${rating}★`,
      count,
    })
  )

  // Calculate low ratings count (1-2 stars)
  const lowRatingsCount =
    (stats.overall.ratingDistribution['1'] || 0) +
    (stats.overall.ratingDistribution['2'] || 0)

  return (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Reviews */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('reviews.totalReviews')}
            </CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.overall.totalReviews}
            </div>
          </CardContent>
        </Card>

        {/* Average Rating */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('reviews.averageRating')}
            </CardTitle>
            <Star className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">
              {stats.overall.averageRating.toFixed(1)} ★
            </div>
          </CardContent>
        </Card>

        {/* Recent Trend */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('reviews.recentTrend')}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {stats.recentTrends.last7Days.toFixed(1)} ★
            </div>
          </CardContent>
        </Card>

        {/* Low Ratings Alert */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('reviews.lowRatingsAlert')}
            </CardTitle>
            <AlertCircle
              className={`h-4 w-4 ${lowRatingsCount > 0 ? 'text-red-500' : 'text-muted-foreground'}`}
            />
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${lowRatingsCount > 0 ? 'text-red-500' : 'text-muted-foreground'}`}
            >
              {lowRatingsCount}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rating Distribution Chart */}
      <Card>
        <CardHeader>
          <CardTitle>{t('reviews.ratingDistribution')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ratingDistData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="rating" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#fbbf24" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top Rated Classes and Staff */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Rated Classes */}
        <Card>
          <CardHeader>
            <CardTitle>{t('reviews.topRatedClasses')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.byClass && stats.byClass.length > 0 ? (
              <div className="space-y-4">
                {stats.byClass.slice(0, 5).map((classData, index) => (
                  <div
                    key={classData.classId}
                    className="flex items-center justify-between border-b pb-3 last:border-0"
                  >
                    <div className="flex-1">
                      <div className="font-medium">{classData.className}</div>
                      <div className="text-sm text-muted-foreground">
                        {classData.count} {t('reviews.reviewCount')}
                      </div>
                    </div>
                    <div className="text-lg font-semibold text-yellow-500 flex items-center gap-1">
                      {classData.avgRating.toFixed(1)}
                      <Star className="h-4 w-4 fill-yellow-500" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('reviews.noReviews')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Top Rated Staff */}
        <Card>
          <CardHeader>
            <CardTitle>{t('reviews.topRatedStaff')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.byStaff && stats.byStaff.length > 0 ? (
              <div className="space-y-4">
                {stats.byStaff.slice(0, 5).map((staffData, index) => (
                  <div
                    key={staffData.staffId}
                    className="flex items-center justify-between border-b pb-3 last:border-0"
                  >
                    <div className="flex-1">
                      <div className="font-medium">{staffData.staffName}</div>
                      <div className="text-sm text-muted-foreground">
                        {staffData.count} {t('reviews.reviewCount')}
                      </div>
                    </div>
                    <div className="text-lg font-semibold text-yellow-500 flex items-center gap-1">
                      {staffData.avgRating.toFixed(1)}
                      <Star className="h-4 w-4 fill-yellow-500" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('reviews.noReviews')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
